import { Badge, Focusable, Tooltip, TooltipTrigger } from '@backstage/ui';
import clsx from 'clsx';
import type { ComponentProps, ReactNode } from 'react';
import { TooltipContentShield } from '../TooltipContentShield';
import styles from './BadgeTooltip.module.css';

const TOOLTIP_DELAY_MS = 50; // BUI's default 600ms hover delay feels slow.

export type BadgeTooltipProps = Omit<ComponentProps<typeof Badge>, 'size'> & {
  tooltip?: ReactNode; // Revealed on hover and focus. Omit for a static badge.
  tooltipProps?: Pick<
    ComponentProps<typeof Tooltip>,
    'className' | 'placement' | 'style'
  >;
  size?: 'default' | 'compact';
};

/**
 * A Badge that reveals a tooltip on hover and focus (could also be called a 'Chip').
 * Focusable supplies the hover and focus wiring that TooltipTrigger needs from its child.
 * Tooltip content runs through TooltipContentShield, so it may contain focusables (a
 * copy button for example).
 */
export function BadgeTooltip({
  tooltip,
  tooltipProps,
  size = 'default',
  className,
  ...badgeProps
}: BadgeTooltipProps) {
  const badge = (
    <Badge
      {...badgeProps}
      className={clsx(
        styles.chip,
        size === 'compact' && styles.chipCompact,
        className,
      )}
      data-interactive={tooltip ? true : undefined}
    />
  );

  if (!tooltip) {
    return badge;
  }

  return (
    <TooltipTrigger delay={TOOLTIP_DELAY_MS} closeDelay={TOOLTIP_DELAY_MS}>
      <Focusable>{badge}</Focusable>
      <Tooltip {...tooltipProps}>
        <TooltipContentShield>{tooltip}</TooltipContentShield>
      </Tooltip>
    </TooltipTrigger>
  );
}
